import { Controller } from "@hotwired/stimulus"

// --- Tier B diagnostics -----------------------------------------------------
// Wrap console + fetch + XHR once so that when an operator sends feedback, recent
// console output and network failures ride along to Clips (redacted). The buffer
// spans the session, so a bug that happened before the panel opened is captured.
const fbDiag = { console: [], network: [], installed: false, startedAt: 0 }
function fbRedact(v) {
  return String(v)
    .replace(/(bearer\s+)[a-z0-9._-]+/gi, "$1<redacted>")
    .replace(/([?&](token|key|secret|password|api[_-]?key|access[_-]?token)=)[^&\s]+/gi, "$1<redacted>")
    .replace(/\b[\w.+-]+@[\w-]+\.[\w.-]+\b/g, "<redacted-email>")
    .replace(/\beyJ[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+/g, "<redacted-jwt>")
}
function fbTrunc(s, n) { return s.length > n ? s.slice(0, n) + "…" : s }
function installFeedbackDiagnostics() {
  if (fbDiag.installed || typeof window === "undefined") return
  fbDiag.installed = true
  fbDiag.startedAt = Date.now()
  const push = (arr, e, max) => { arr.push(e); if (arr.length > max) arr.shift() }
  ;["debug", "log", "info", "warn", "error"].forEach((level) => {
    const orig = console[level].bind(console)
    console[level] = (...args) => {
      try {
        const err = args.find((a) => a instanceof Error)
        push(fbDiag.console, {
          timestampMs: Date.now(), elapsedMs: Math.max(0, Date.now() - fbDiag.startedAt), level,
          message: fbTrunc(fbRedact(args.map((a) => (a instanceof Error ? `${a.name}: ${a.message}` : typeof a === "object" ? JSON.stringify(a) : a)).join(" ")), 4000),
          stack: err?.stack ? fbTrunc(fbRedact(err.stack), 4000) : undefined,
        }, 50)
      } catch (_) {}
      orig(...args)
    }
  })
  const of = window.fetch.bind(window)
  window.fetch = async (input, init) => {
    const started = Date.now()
    const method = (init?.method || (typeof input !== "string" && !(input instanceof URL) ? input.method : "GET") || "GET").toUpperCase()
    const url = fbTrunc(fbRedact(typeof input === "string" ? input : input instanceof URL ? input.href : input.url), 2000)
    try {
      const res = await of(input, init)
      push(fbDiag.network, { timestampMs: started, elapsedMs: Math.max(0, started - fbDiag.startedAt), type: "fetch", method, url, status: res.status, ok: res.ok, durationMs: Date.now() - started }, 50)
      return res
    } catch (e) {
      push(fbDiag.network, { timestampMs: started, elapsedMs: Math.max(0, started - fbDiag.startedAt), type: "fetch", method, url, ok: false, durationMs: Date.now() - started }, 50)
      throw e
    }
  }
  const OX = window.XMLHttpRequest
  if (OX) {
    const open = OX.prototype.open, send = OX.prototype.send
    OX.prototype.open = function (m, u) { this.__fb = { method: (m || "GET").toUpperCase(), url: fbTrunc(fbRedact(String(u)), 2000), t: 0 }; return open.apply(this, arguments) }
    OX.prototype.send = function () {
      const meta = this.__fb
      if (meta) { meta.t = Date.now(); this.addEventListener("loadend", () => push(fbDiag.network, { timestampMs: meta.t, elapsedMs: Math.max(0, meta.t - fbDiag.startedAt), type: "xhr", method: meta.method, url: meta.url, status: this.status || undefined, ok: this.status >= 200 && this.status < 400, durationMs: Date.now() - meta.t }, 50)) }
      return send.apply(this, arguments)
    }
  }
}
function snapshotFeedbackDiagnostics() {
  return { consoleLogs: fbDiag.console.slice(-50), networkRequests: fbDiag.network.slice(-50) }
}

// --- Persistent recording session ------------------------------------------
// The feedback widget is rendered by the account layout, so a Turbo navigation
// ("switching screens") tears down its controller and rebuilds a fresh one. If
// the recording lived on the controller instance, disconnect() would stop the
// stream and switching screens would wipe an in-progress recording — you could
// never capture a bug that spans more than one page.
//
// So the live recording (MediaRecorder, its streams, the chunks, the live
// transcript, the typed note, the timer origin) lives HERE, at module scope, and
// survives every navigation. The controller is a thin view that re-adopts this
// session on connect() and reflects it — never tearing it down just because the
// element left the DOM. `view` points at whichever controller is currently
// mounted so the recorder's async callbacks can update the on-screen UI.
const session = {
  active: false, recorder: null, streams: [], chunks: [], audioCtx: null,
  recognition: null, transcript: "", mimeType: "video/webm", hasMic: false,
  screenSupported: false, videoBlob: null, note: "", seconds: 0, startedAt: 0,
  view: null,
}

function stopSession() {
  if (session.recorder && session.recorder.state !== "inactive") session.recorder.stop() // → onstop → onSessionStopped
  if (session.recognition) { try { session.recognition.stop() } catch (_) {} }
  const v = session.view
  if (v) { v.stopTimerDisplay(); if (v.hasStopBtnTarget) v.stopBtnTarget.style.display = "none" }
}

function onSessionStopped() {
  session.active = false
  const type = (session.mimeType || "video/webm").split(";")[0] // "video/webm" or "audio/webm"
  session.videoBlob = new Blob(session.chunks, { type })
  session.seconds = session.startedAt ? Math.floor((Date.now() - session.startedAt) / 1000) : session.seconds
  session.streams.forEach((s) => s.getTracks().forEach((t) => t.stop()))
  session.streams = []
  if (session.audioCtx) { try { session.audioCtx.close() } catch (_) {} session.audioCtx = null }
  const v = session.view
  if (v) {
    v.showPreview(session.videoBlob, type)
    if (v.hasRecordBtnTarget) v.recordBtnTarget.style.display = ""
    if (v.hasRecordLabelTarget) v.recordLabelTarget.textContent = "Re-record"
    v.setStatus("Recording ready. Add a note if you like, then send.")
  }
}

function resetSession() {
  if (session.recorder && session.recorder.state !== "inactive") { try { session.recorder.stop() } catch (_) {} }
  session.streams.forEach((s) => s.getTracks().forEach((t) => t.stop()))
  if (session.audioCtx) { try { session.audioCtx.close() } catch (_) {} }
  if (session.recognition) { try { session.recognition.stop() } catch (_) {} }
  Object.assign(session, {
    active: false, recorder: null, streams: [], chunks: [], audioCtx: null,
    recognition: null, transcript: "", videoBlob: null, note: "", seconds: 0, startedAt: 0,
  })
}

// Feedback widget. On desktop it records the screen + microphone; on mobile —
// where the Screen Capture API (getDisplayMedia) doesn't exist — it falls back to
// a voice recording. Either way it live-transcribes speech (Web Speech API) and
// lets the user type a note, then uploads the recording straight to S3 (presigned
// PUT) and posts the feedback to the super-admin inbox.
//
// Degrades gracefully: no mic -> typed note alone; no SpeechRecognition -> no
// transcript (voice is still captured in the recording). The recording itself
// lives in the module-level `session` above so it survives page navigation.
export default class extends Controller {
  static targets = ["panel", "open", "message", "timer", "status", "intro", "recordLabel",
                    "preview", "imgPreview", "previewWrap", "recordBtn", "stopBtn", "submitBtn",
                    "transcriptNote", "file"]
  static values = { createUrl: String }

  connect() {
    installFeedbackDiagnostics()
    // Decoupled open trigger: the mobile bottom-nav Feedback button lives in a
    // different DOM subtree, so it dispatches window "feedback:open" instead of
    // targeting this controller directly. Listen while connected; clean up on
    // disconnect so a Turbo navigation doesn't leak listeners.
    this.onOpenRequest = () => this.openPanel()
    window.addEventListener("feedback:open", this.onOpenRequest)
    session.view = this

    // Screen capture is desktop-only. Mobile browsers either lack getDisplayMedia
    // or reject it, so fall back to a voice recording there.
    session.screenSupported =
      !!(navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === "function") &&
      !/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)

    if (!session.screenSupported && !session.videoBlob && !session.active) {
      if (this.hasRecordLabelTarget) this.recordLabelTarget.textContent = "Record voice"
      if (this.hasIntroTarget) {
        this.introTarget.textContent =
          "Record a voice note describing the problem, or just type one — it goes straight to the Stowlane team."
      }
    }

    // Re-adopt any recording that was in progress (or finished but unsent) before
    // this navigation, so the tool reappears with its live state instead of resetting.
    this.restore()
  }

  disconnect() {
    if (this.onOpenRequest) window.removeEventListener("feedback:open", this.onOpenRequest)
    // Stop only the on-screen ticker and preserve the typed note. Crucially, do
    // NOT stop the recorder or its streams here — an active recording must ride
    // through the navigation. It's torn down only on stop()/submit()/reset.
    this.stopTimerDisplay()
    if (this.hasMessageTarget) session.note = this.messageTarget.value
    if (session.view === this) session.view = null
  }

  // Reflect the persistent session on a freshly-mounted controller so switching
  // screens never loses an in-progress or just-finished recording.
  restore() {
    if (this.hasMessageTarget && session.note) this.messageTarget.value = session.note
    if (session.active) {
      this.openPanel()
      if (this.hasRecordBtnTarget) this.recordBtnTarget.style.display = "none"
      if (this.hasStopBtnTarget) this.stopBtnTarget.style.display = ""
      this.startTimerDisplay()
      this.setStatus(session.screenSupported
        ? (session.hasMic ? "Recording 🎤 — describe the problem out loud." : "Recording (no mic) — allow mic access to narrate.")
        : "Recording voice — describe the problem.")
    } else if (session.videoBlob) {
      this.openPanel()
      this.showPreview(session.videoBlob, session.videoBlob.type || session.mimeType)
      if (this.hasRecordLabelTarget) this.recordLabelTarget.textContent = "Re-record"
      this.setStatus("Recording ready. Add a note if you like, then send.")
    }
  }

  toggle() { this.panelTarget.classList.toggle("hidden") }

  // Always reveal the panel (used by the decoupled "feedback:open" trigger from
  // the mobile bottom nav — a toggle could hide an already-open panel).
  openPanel() { this.panelTarget.classList.remove("hidden") }

  // Hiding the panel must NOT stop an active recording — the operator may be
  // navigating around the app while capturing a bug. Stopping is explicit (the
  // Stop button, or the browser's own "Stop sharing" control).
  closePanel() { this.panelTarget.classList.add("hidden") }

  // --- Recording ----------------------------------------------------------

  async record() {
    this.setStatus(session.screenSupported ? "Starting screen share…" : "Starting microphone…")
    try {
      let stream
      session.chunks = []
      session.transcript = ""
      session.videoBlob = null
      if (session.screenSupported) {
        // Acquire the mic FIRST: on macOS getDisplayMedia grabs the audio
        // subsystem, and a mic opened afterward comes back silent.
        let micStream = null
        try { micStream = await navigator.mediaDevices.getUserMedia({ audio: true }) }
        catch (_) { /* no mic — fall back to any shared tab/system audio */ }

        const display = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 15 }, audio: true })
        session.streams = [display]
        if (micStream) session.streams.push(micStream)

        // MediaRecorder only encodes ONE audio track from a MediaStream, so a
        // (usually silent) system-audio track can crowd out the mic. Mix the mic
        // and any shared tab/system audio into a single track via Web Audio.
        const audioSources = []
        if (display.getAudioTracks().length) audioSources.push(new MediaStream(display.getAudioTracks()))
        if (micStream && micStream.getAudioTracks().length) audioSources.push(new MediaStream(micStream.getAudioTracks()))
        let audioTrack = null
        if (audioSources.length > 1) {
          session.audioCtx = new (window.AudioContext || window.webkitAudioContext)()
          const dest = session.audioCtx.createMediaStreamDestination()
          audioSources.forEach((s) => session.audioCtx.createMediaStreamSource(s).connect(dest))
          audioTrack = dest.stream.getAudioTracks()[0]
        } else if (audioSources.length === 1) {
          audioTrack = audioSources[0].getAudioTracks()[0]
        }
        session.hasMic = !!(micStream && micStream.getAudioTracks().length)

        stream = new MediaStream([...display.getVideoTracks(), ...(audioTrack ? [audioTrack] : [])])
        // The user ending the share from the browser's own control stops us too.
        display.getVideoTracks()[0].addEventListener("ended", () => stopSession())
        session.mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
          ? "video/webm;codecs=vp9,opus" : "video/webm"
      } else {
        // Mobile: voice only.
        const mic = await navigator.mediaDevices.getUserMedia({ audio: true })
        session.streams = [mic]
        stream = mic
        session.mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus" : "audio/webm"
      }

      session.recorder = new MediaRecorder(stream, { mimeType: session.mimeType })
      session.recorder.ondataavailable = (e) => { if (e.data && e.data.size) session.chunks.push(e.data) }
      session.recorder.onstop = () => onSessionStopped()
      // Timeslice so chunks flush periodically — a recording that spans several
      // page changes still has its data buffered if the tab is closed abruptly.
      session.recorder.start(1000)
      session.active = true
      session.startedAt = Date.now()

      this.startTranscription()
      this.startTimerDisplay()
      this.recordBtnTarget.style.display = "none"
      this.stopBtnTarget.style.display = ""
      this.setStatus(session.screenSupported
        ? (session.hasMic ? "Recording 🎤 — describe the problem out loud." : "Recording (no mic) — allow mic access to narrate.")
        : "Recording voice — describe the problem.")
    } catch (_) {
      session.active = false
      this.resetButtons()
      this.setStatus(session.screenSupported
        ? "Couldn't start screen recording. Please allow screen sharing and try again."
        : "Couldn't start the microphone. Please allow mic access, or just type a note below.")
    }
  }

  stop() { stopSession() }

  // --- File attach (phone screen recording or screenshot) -----------------

  chooseFile() { if (this.hasFileTarget) this.fileTarget.click() }

  fileChosen(event) {
    const file = event.target.files && event.target.files[0]
    if (!file) return
    session.videoBlob = file
    session.mimeType = file.type || "application/octet-stream"
    this.showPreview(file, session.mimeType)
    this.setStatus("Attached. Add a note if you like, then send.")
  }

  showPreview(blob, type) {
    const url = URL.createObjectURL(blob)
    if (type.startsWith("image")) {
      this.imgPreviewTarget.src = url
      this.imgPreviewTarget.classList.remove("hidden")
      this.previewTarget.classList.add("hidden")
    } else {
      const v = this.previewTarget
      v.src = url
      v.classList.remove("hidden")
      if (this.hasImgPreviewTarget) this.imgPreviewTarget.classList.add("hidden")
      // MediaRecorder WebM has no duration in its header and paints no poster
      // frame until decoded, so the preview shows a black 0:00. Nudge it: seek
      // past the end to force the browser to compute the real duration, then to
      // a hair in so the first frame renders as the poster.
      this.fixWebmPreview(v)
    }
    this.previewWrapTarget.classList.remove("hidden")
  }

  fixWebmPreview(v) {
    const seekToStart = () => { try { v.currentTime = 0.1 } catch (_) {} }
    v.addEventListener("loadedmetadata", () => {
      if (v.duration === Infinity || isNaN(v.duration)) {
        const onUpdate = () => {
          v.removeEventListener("timeupdate", onUpdate)
          seekToStart()
        }
        v.addEventListener("timeupdate", onUpdate)
        try { v.currentTime = 1e101 } catch (_) { seekToStart() }
      } else {
        seekToStart()
      }
    }, { once: true })
  }

  // --- Live transcription -------------------------------------------------

  startTranscription() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) { if (this.hasTranscriptNoteTarget) this.transcriptNoteTarget.classList.remove("hidden"); return }
    session.recognition = new SR()
    session.recognition.continuous = true
    session.recognition.interimResults = true
    session.recognition.lang = "en-US"
    session.recognition.onresult = (event) => {
      let text = ""
      for (let i = 0; i < event.results.length; i++) text += event.results[i][0].transcript + " "
      session.transcript = text.trim()
    }
    try { session.recognition.start() } catch (_) {}
  }

  // --- Submit -------------------------------------------------------------

  async submit() {
    this.submitBtnTarget.disabled = true
    this.setStatus(session.videoBlob ? "Uploading recording…" : "Sending…")
    try {
      // One multipart request: the note/transcript/page/diagnostics + the media.
      // The server forwards the recording to Clips (transcript, frames, agent).
      const form = new FormData()
      form.append("feedback[message]", (this.hasMessageTarget ? this.messageTarget.value : session.note) || "")
      form.append("feedback[transcript]", session.transcript || "")
      form.append("feedback[page]", window.location.href)
      form.append("feedback[recording_seconds]", String(session.seconds || 0))
      form.append("diagnostics", JSON.stringify(snapshotFeedbackDiagnostics()))
      if (session.videoBlob) {
        const type = session.videoBlob.type || "application/octet-stream"
        const ext = type.startsWith("image") ? "png" : type.startsWith("audio") ? "webm" : "webm"
        const name = session.videoBlob.name || `recording.${ext}`
        form.append("media", session.videoBlob, name)
      }

      const res = await fetch(this.createUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrf(), Accept: "application/json" },
        body: form,
      })

      if (res.ok) {
        resetSession()
        this.panelTarget.innerHTML =
          '<div class="p-6 text-center"><p class="text-base font-semibold text-secondary-900">Thanks — got it.</p>' +
          '<p class="mt-1 text-sm text-secondary-500">Your feedback was sent to the Stowlane team.</p></div>'
      } else {
        this.submitBtnTarget.disabled = false
        this.setStatus("Couldn't send — please try again.")
      }
    } catch (_) {
      this.submitBtnTarget.disabled = false
      this.setStatus("Couldn't send — please try again.")
    }
  }

  // --- Helpers ------------------------------------------------------------

  // The timer reads its elapsed time from session.startedAt, so it shows the
  // TRUE recording length even after a navigation rebuilt this controller.
  startTimerDisplay() {
    this.stopTimerDisplay()
    const tick = () => {
      const secs = session.startedAt ? Math.floor((Date.now() - session.startedAt) / 1000) : 0
      session.seconds = secs
      if (this.hasTimerTarget) {
        const m = Math.floor(secs / 60)
        const s = String(secs % 60).padStart(2, "0")
        this.timerTarget.textContent = `${m}:${s}`
      }
    }
    tick()
    this.timerInterval = setInterval(tick, 1000)
  }

  stopTimerDisplay() { if (this.timerInterval) { clearInterval(this.timerInterval); this.timerInterval = null } }

  resetButtons() {
    if (this.hasRecordBtnTarget) this.recordBtnTarget.style.display = ""
    if (this.hasStopBtnTarget) this.stopBtnTarget.style.display = "none"
    this.stopTimerDisplay()
  }

  setStatus(text) { if (this.hasStatusTarget) this.statusTarget.textContent = text }

  csrf() { return document.querySelector('meta[name="csrf-token"]')?.content }
}
